import axios from "axios";
import {
  Ban,
  CheckCircle2,
  MinusCircle,
  LockKeyhole,
  Percent,
  Plus,
  RefreshCw,
  ShieldAlert,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Card, CardBody, Chip, Spinner } from "@heroui/react";
import {
  type CustomerCarrier,
  useAddCustomerCarrier,
  useCustomerCarriers,
  useRemoveCustomerCarrier,
} from "@/api/use-customer-carriers";
import { PageHeader, HairlineStats } from "@/components/page-chrome";
import { useToast } from "@/hooks/use-toast";

type CarrierErrorBody = {
  code?: string;
  error?: string;
  message?: string;
};

function carrierInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDiscount(pct: number) {
  return `${Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(2)}%`;
}

function getCarrierErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError<CarrierErrorBody>(error)) {
    const body = error.response?.data;
    if (
      error.response?.status === 403 ||
      body?.code === "CARRIER_ACCESS_BLOCKED"
    ) {
      return body?.message ?? fallback;
    }
    return body?.message ?? body?.error ?? fallback;
  }
  return fallback;
}

function stateTone(state: CustomerCarrier["state"]) {
  switch (state) {
    case "active":
      return "success" as const;
    case "removed":
      return "warning" as const;
    case "blocked":
      return "danger" as const;
    case "not_added":
      return "default" as const;
  }
}

function stateIcon(state: CustomerCarrier["state"]) {
  switch (state) {
    case "active":
      return CheckCircle2;
    case "removed":
      return MinusCircle;
    case "blocked":
      return ShieldAlert;
    case "not_added":
      return Truck;
  }
}

export default function CarriersScreen() {
  const { t } = useTranslation("app");
  const { toast } = useToast();
  const { data, isLoading, isError, refetch, isFetching } =
    useCustomerCarriers();
  const addCarrier = useAddCustomerCarrier();
  const removeCarrier = useRemoveCustomerCarrier();

  const carriers = data?.data ?? [];
  const activeCount = carriers.filter((c) => c.state === "active").length;
  const availableCount = carriers.filter(
    (c) => c.state === "not_added" || c.state === "removed"
  ).length;
  const blockedCount = carriers.filter((c) => c.state === "blocked").length;

  const handleAdd = (carrier: CustomerCarrier) => {
    addCarrier.mutate(carrier.id, {
      onSuccess: () => {
        toast({
          title:
            carrier.state === "removed"
              ? t("carriers.toast.readdedTitle")
              : t("carriers.toast.addedTitle"),
          description: t("carriers.toast.addedDescription", {
            carrier: carrier.name,
          }),
        });
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          title: t("carriers.toast.errorTitle"),
          description: getCarrierErrorMessage(
            error,
            t("carriers.toast.addError")
          ),
        });
      },
    });
  };

  const handleRemove = (carrier: CustomerCarrier) => {
    removeCarrier.mutate(carrier.id, {
      onSuccess: () => {
        toast({
          title: t("carriers.toast.removedTitle"),
          description: t("carriers.toast.removedDescription", {
            carrier: carrier.name,
          }),
        });
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          title: t("carriers.toast.errorTitle"),
          description: getCarrierErrorMessage(
            error,
            t("carriers.toast.removeError")
          ),
        });
      },
    });
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t("carriers.eyebrow")}
        title={t("carriers.title")}
        italic={t("carriers.italic")}
        lede={t("carriers.lede")}
        right={
          <Button
            variant="bordered"
            onPress={() => void refetch()}
            isLoading={isFetching && !isLoading}
            startContent={<RefreshCw className="h-4 w-4" />}
          >
            {t("carriers.refresh")}
          </Button>
        }
      />

      <HairlineStats
        items={[
          {
            value: activeCount,
            label: t("carriers.stats.active"),
            accent: "success",
          },
          {
            value: availableCount,
            label: t("carriers.stats.available"),
            accent: "primary",
          },
          {
            value: blockedCount,
            label: t("carriers.stats.restricted"),
            accent: blockedCount > 0 ? "danger" : undefined,
          },
        ]}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" label={t("carriers.loading")} />
        </div>
      ) : isError ? (
        <Card shadow="none" className="border border-danger-200 bg-danger-50">
          <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 text-danger-700">
              <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">{t("carriers.errorTitle")}</div>
                <div className="text-sm text-danger-700/80">
                  {t("carriers.errorBody")}
                </div>
              </div>
            </div>
            <Button
              color="danger"
              variant="flat"
              onPress={() => void refetch()}
              startContent={<RefreshCw className="h-4 w-4" />}
            >
              {t("carriers.retry")}
            </Button>
          </CardBody>
        </Card>
      ) : carriers.length === 0 ? (
        <Card shadow="none" className="border border-divider">
          <CardBody className="py-12 text-center">
            <Truck className="h-8 w-8 mx-auto text-default-400" />
            <h2 className="text-lg font-semibold mt-3">
              {t("carriers.emptyTitle")}
            </h2>
            <p className="text-sm text-default-500 mt-1">
              {t("carriers.emptyBody")}
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {carriers.map((carrier) => (
            <CarrierCard
              key={carrier.id}
              carrier={carrier}
              onAdd={() => handleAdd(carrier)}
              onRemove={() => handleRemove(carrier)}
              addPending={
                addCarrier.isPending && addCarrier.variables === carrier.id
              }
              removePending={
                removeCarrier.isPending &&
                removeCarrier.variables === carrier.id
              }
              formatDiscount={formatDiscount}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CarrierCard({
  carrier,
  onAdd,
  onRemove,
  addPending,
  removePending,
  formatDiscount,
}: {
  carrier: CustomerCarrier;
  onAdd: () => void;
  onRemove: () => void;
  addPending: boolean;
  removePending: boolean;
  formatDiscount: (pct: number) => string;
}) {
  const { t } = useTranslation("app");
  const Icon = stateIcon(carrier.state);
  const isBlocked = carrier.state === "blocked";
  const canRemove = carrier.state === "active";
  const canAdd = carrier.state === "not_added" || carrier.state === "removed";
  const customDiscount = carrier.connection?.customDiscountPercentage;

  return (
    <Card shadow="sm" className="border border-divider">
      <CardBody className="p-5 space-y-5">
        <div className="flex items-start gap-4">
          <Avatar
            src={carrier.logoUrl ?? undefined}
            name={carrierInitials(carrier.name)}
            radius="md"
            classNames={{
              base: "bg-primary-50 text-primary shrink-0",
              name: "font-semibold text-xs",
            }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold m-0 truncate">
                {carrier.name}
              </h2>
              <Chip
                size="sm"
                variant="flat"
                color={stateTone(carrier.state)}
                startContent={<Icon className="h-3 w-3" />}
              >
                {t(`carriers.state.${carrier.state}`)}
              </Chip>
            </div>
            <div className="text-xs text-default-500 mt-1">{carrier.code}</div>
          </div>
        </div>

        <div className="min-h-[44px] rounded-medium bg-default-50 px-3 py-2 text-sm text-default-600">
          {isBlocked ? (
            <span className="inline-flex items-start gap-2">
              <Ban className="h-4 w-4 shrink-0 mt-0.5 text-danger" />
              <span>{t("carriers.blockedBody")}</span>
            </span>
          ) : customDiscount != null ? (
            <span className="inline-flex items-start gap-2">
              <Percent className="h-4 w-4 shrink-0 mt-0.5 text-success" />
              <span>
                {t("carriers.customDiscount", {
                  percent: formatDiscount(customDiscount),
                })}
              </span>
            </span>
          ) : carrier.connection?.tierId ? (
            <span className="inline-flex items-start gap-2">
              <Percent className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              <span>{t("carriers.tierDiscount")}</span>
            </span>
          ) : (
            <span>{t(`carriers.body.${carrier.state}`)}</span>
          )}
        </div>

        <div className="flex justify-end">
          {!carrier.customerCanToggle ? (
            <Button
              variant="flat"
              isDisabled
              startContent={<LockKeyhole className="h-4 w-4" />}
            >
              {t("carriers.managedByEzx")}
            </Button>
          ) : canRemove ? (
            <Button
              color="danger"
              variant="flat"
              onPress={onRemove}
              isLoading={removePending}
              startContent={<MinusCircle className="h-4 w-4" />}
            >
              {t("carriers.remove")}
            </Button>
          ) : canAdd ? (
            <Button
              color="primary"
              onPress={onAdd}
              isLoading={addPending}
              startContent={
                carrier.state === "removed" ? (
                  <RefreshCw className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )
              }
            >
              {carrier.state === "removed"
                ? t("carriers.readd")
                : t("carriers.add")}
            </Button>
          ) : (
            <Button
              variant="flat"
              color="danger"
              isDisabled
              startContent={<ShieldAlert className="h-4 w-4" />}
            >
              {t("carriers.restricted")}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
